import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  IconType,
  injectEnvironment,
  LayoutService,
  LoadingService,
  NotificationService,
} from '@cf/core';
import { EventRejectTentativeService } from '@cf/features/events';
import { IconComponent } from '@cf/shared/ui/icons';
import { LoaderSpinnerContainerComponent } from '@cf/shared/ui/loading';
import { MainLogoComponent } from '@cf/shared/ui/logos';
import { TextComponent } from '@cf/shared/ui/typography';
import { AppEventApiService, EventStatusEnum } from '@cf/temp/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { CookieService } from 'ngx-cookie-service';
import { IsStandaloneBookingApp } from './tokens/is-standalone-booking-app';

@Component({
  templateUrl: './booking-wrapper.component.html',
  styleUrls: ['./pages/booking/booking-page.component.scss'],
  imports: [
    LoaderSpinnerContainerComponent,
    IconComponent,
    TextComponent,
    RouterLink,
    MainLogoComponent,
    NgxTolgeeModule,
    AsyncPipe,
  ],
})
export class BookingWrapperComponent implements OnInit, OnDestroy {
  public readonly loadingService = inject(LoadingService);
  public readonly eventApiService = inject(AppEventApiService);
  public readonly modalService = inject(NgbModal);
  public readonly notificationService = inject(NotificationService);
  public readonly layoutService = inject(LayoutService);
  public readonly router = inject(Router);
  public readonly route = inject(ActivatedRoute);
  public readonly cd = inject(ChangeDetectorRef);
  public readonly cookieService = inject(CookieService);
  #rejectTentativeService = inject(EventRejectTentativeService);
  translateService = inject(TranslateService);
  tentative!: { title: string; icon: IconType; description?: string };

  isStandalone = inject(IsStandaloneBookingApp);
  environment = injectEnvironment();

  constructor() {
    this.layoutService.hideHeader.next(true);
  }

  get redirection_url_ms_booking() {
    return this.cookieService.get('redirection_url_ms_booking');
  }

  ngOnInit() {
    this.checkToken();
  }

  ngOnDestroy() {
    this.layoutService.hideHeader.next(false);
  }

  private checkToken(): void {
    this.route.queryParams.subscribe(
      ({
        cancelEventToken,
        rescheduleEventToken,
        acceptTentativeEventToken,
        rejectTentativeEventToken,
      }) => {
        if (cancelEventToken) {
          void this.router.navigate(['/cancel'], {
            queryParams: { cancelEventToken },
          });
        } else if (rescheduleEventToken) {
          void this.router.navigate(['/reschedule'], {
            queryParams: { rescheduleEventToken },
          });
        } else if (acceptTentativeEventToken) {
          this.acceptTentative(acceptTentativeEventToken);
        } else if (rejectTentativeEventToken) {
          this.rejectTentative(rejectTentativeEventToken);
        } else {
          this.redirectToHome();
        }
      },
    );
  }

  private checkTentativeEvent(token: string) {
    return this.eventApiService.event(token);
  }

  private acceptTentative(token: string) {
    this.eventApiService.acceptTentative(token, true).subscribe({
      next: () => {
        this.tentative = {
          title: this.translateService.instant('booking.accepted'),
          description: this.translateService.instant('booking.accepted_text'),
          icon: 'ok-status',
        };
      },
      error: (err: HttpErrorResponse) => {
        this.handleTentativeError(err);
      },
    });
  }

  private rejectTentative(token: string) {
    this.checkTentativeEvent(token).subscribe((event) => {
      if (event.is_cancelled) {
        this.tentative = {
          title: this.translateService.instant('booking.cancelled'),
          icon: 'error-status',
        };
        return;
      }
      if (event.status === EventStatusEnum.PENDING) {
        const modal = this.#rejectTentativeService.openDialog(event);
        modal.closed$.subscribe((reason: string | undefined) => {
          if (typeof reason !== 'string') return;
          this.eventApiService.rejectTentative(token, reason, true).subscribe({
            next: () => {
              this.tentative = {
                title: this.translateService.instant('booking.rejected'),
                icon: 'error-status',
              };
              this.notificationService.warning(
                this.translateService.instant('booking.rejected'),
                '',
              );
              this.redirectToHome();
            },
            error: (err: HttpErrorResponse) => {
              this.handleTentativeError(err);
            },
          });
        });
        modal.dismissed$.subscribe(() => {
          this.redirectToHome();
        });
      } else {
        this.tentative = {
          title:
            event.status === EventStatusEnum.ACCEPTED
              ? this.translateService.instant('booking.accepted_notify')
              : this.translateService.instant('booking.rejected_notify'),
          icon: event.status === EventStatusEnum.ACCEPTED ? 'ok-status' : 'error-status',
        };
        this.cd.detectChanges();
      }
    });
  }

  private handleTentativeError(err: HttpErrorResponse) {
    if (err.status === 404) {
      this.tentative = {
        title: this.translateService.instant('booking.booking_not_found'),
        icon: 'error-status',
      };
      this.cd.detectChanges();
    }
    if (err.status === 400) {
      const message = err.error.errors?.non_field_errors?.find(Boolean)?.message;
      if (message) {
        this.tentative = {
          title: message,
          icon: message?.includes('accepted') ? 'ok-status' : 'error-status',
        };
        this.cd.detectChanges();
      } else {
        this.redirectToHome();
        this.notificationService.error(this.translateService.instant('booking.error'));
      }
    }
  }

  private redirectToHome() {
    if (this.isStandalone) {
      if (this.redirection_url_ms_booking) {
        void this.router.navigate([this.redirection_url_ms_booking]);
      } else {
        window.location.href = this.environment.appUrl;
      }
    } else {
      void this.router.navigate(['/']);
    }
  }
}
